import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useMemo, useState } from 'react';

import { LinkContractDialog } from '@/features/software/contract-and-cost/contract';
import { usePaginationSearch, useSortingSearch } from '@/lib/listPage';
import { ContractDealTable } from '@/routes/_staff/software/_layout/contract-and-cost/_contract-and-cost/deal/_deal/contract/-components/ContractDealTable';
import { ContractSearchFilterBar } from '@/routes/_staff/software/_layout/contract-and-cost/_contract-and-cost/deal/_deal/contract/-components/ContractSearchFilterBar';
import { useContractDealList } from '@/routes/_staff/software/_layout/contract-and-cost/_contract-and-cost/deal/_deal/contract/-query';
import {
  ContractDealSearchSchema,
  toContractDealOrderBy,
} from '@/routes/_staff/software/_layout/contract-and-cost/_contract-and-cost/deal/_deal/contract/-shared/contractDealSearchSchema';
import { useContractDealFilters } from '@/routes/_staff/software/_layout/contract-and-cost/_contract-and-cost/deal/_deal/contract/-shared/useContractDealFilters';

export const Route = createFileRoute(
  '/_staff/software/$id/_$id/contract-and-cost/_contract-and-cost/deal/_deal/contract/',
)({
  component: ContractTab,
  validateSearch: zodValidator(ContractDealSearchSchema),
});

function ContractTab() {
  const { id: softwareId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);

  const [pagination, setPagination] = usePaginationSearch({
    search,
    onSearchChange: (s) => navigate({ search: s }),
  });

  const [sorting, setSorting] = useSortingSearch({
    search,
    onSearchChange: (s) => navigate({ search: s }),
  });

  const orderByParam = useMemo(
    () => toContractDealOrderBy(search.sortColumn),
    [search.sortColumn],
  );

  const { data, isPending } = useContractDealList(
    {
      page: search.pageIndex + 1,
      pageSize: search.pageSize,
      freeText: search.freeText,
      includeInactive: search.showTerminated || undefined,
      contractStartFrom: search.contractStartFrom,
      contractStartTo: search.contractStartTo,
      contractEndFrom: search.contractEndFrom,
      contractEndTo: search.contractEndTo,
      orderBy: orderByParam,
      orderDirection: search.sortOrder,
    },
    softwareId,
  );

  const {
    contractStartDateFilter,
    handleContractStartDateFilterChange,
    contractEndDateFilter,
    handleContractEndDateFilterChange,
    handleShowTerminatedChange,
  } = useContractDealFilters(search, navigate);

  return (
    <>
      <ContractSearchFilterBar
        searchValue={search.freeText ?? ''}
        onSearchChange={(e) =>
          navigate({
            search: (prev) => ({
              ...prev,
              freeText: e.target.value || undefined,
              pageIndex: 0,
            }),
          })
        }
        contractStartDateFilter={contractStartDateFilter}
        onContractStartDateFilterChange={handleContractStartDateFilterChange}
        contractEndDateFilter={contractEndDateFilter}
        onContractEndDateFilterChange={handleContractEndDateFilterChange}
        showTerminated={search.showTerminated}
        onShowTerminatedChange={handleShowTerminatedChange}
        onLinkContract={() => setIsLinkDialogOpen(true)}
      />
      <ContractDealTable
        data={data?.items ?? []}
        isPending={isPending}
        totalCount={data?.count}
        pageCount={data?.maxPage}
        pagination={pagination}
        onPaginationChange={setPagination}
        sorting={sorting}
        onSortingChange={setSorting}
        hideSoftwareColumn
        className="flex-1 min-h-0"
      />
      <LinkContractDialog
        open={isLinkDialogOpen}
        onOpenChange={setIsLinkDialogOpen}
        softwareId={softwareId}
      />
    </>
  );
}
